import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Plus, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProjects, useRegularProjects, useCreateProject } from '../hooks/useProjects';
import { useUserStore } from '../store/userStore';
import { useEffectiveRole } from '../hooks/useEffectiveRole';
import { DashboardHeader } from '../components/DashboardHeader';
import { useProjectStore, type StatusFilterKey } from '../store/projectStore';
import { RiskCard } from '../components/RiskCard';
import { FilterCard, type FilterOption } from '../components/FilterCard';
import { track } from '../lib/telemetry';
import type { Project, ProjectStatus } from '../types';

function ProjectSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex justify-between items-start">
        <div className="skeleton h-5 w-36 rounded" />
        <div className="skeleton h-8 w-12 rounded" />
      </div>
      <div className="skeleton h-3 w-full rounded" />
      <div className="skeleton h-3 w-4/5 rounded" />
      <div className="flex justify-between mt-2">
        <div className="skeleton h-5 w-20 rounded-full" />
        <div className="skeleton h-3 w-16 rounded" />
      </div>
    </div>
  );
}

function AddProjectModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const project = await createProject.mutateAsync({
      name: name.trim(),
      description: description.trim() || undefined,
      status: 'planning',
    });
    navigate(`/projects/${project.projectId}`);
    onClose();
  };

  return createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-2xl p-6 w-full max-w-md pointer-events-auto shadow-xl"
        >
          <h3 className="font-semibold text-gray-900 dark:text-white text-title mb-6">
            New Project
          </h3>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
                Project Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q1 Platform Refresh"
                required
                autoFocus
                className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief project description..."
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 resize-none"
              />
            </div>
            {createProject.isError && (
              <p className="text-meta text-red-500 dark:text-red-400">
                {(createProject.error as Error).message}
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 rounded-xl text-meta text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={createProject.isPending}
                className="flex-1 py-2 rounded-xl text-meta font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {createProject.isPending ? 'Creating...' : 'Create Project'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </>,
    document.body,
  );
}

const STATUS_GROUPS: Record<StatusFilterKey, ProjectStatus[]> = {
  at_risk: ['at_risk'],
  on_track: ['on_track', 'active'],
  planning: ['planning', 'draft'],
  complete: ['complete'],
};

const FILTER_OPTIONS: Omit<FilterOption, 'count'>[] = [
  {
    value: 'at_risk',
    label: 'At Risk',
    colorClass: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
    activeClass: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    borderClass: 'border-red-400 dark:border-red-600',
    ringClass: 'ring-red-400 dark:ring-red-600',
  },
  {
    value: 'on_track',
    label: 'On Track',
    colorClass: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
    activeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    borderClass: 'border-yellow-500 dark:border-yellow-600',
    ringClass: 'ring-yellow-500 dark:ring-yellow-600',
  },
  {
    value: 'planning',
    label: 'Planning',
    colorClass: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    activeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    borderClass: 'border-blue-400 dark:border-blue-600',
    ringClass: 'ring-blue-400 dark:ring-blue-600',
  },
  {
    value: 'complete',
    label: 'Complete',
    colorClass: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400',
    activeClass: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    borderClass: 'border-green-400 dark:border-green-600',
    ringClass: 'ring-green-400 dark:ring-green-600',
  },
];

export function PMDashboard({ onBackToToday }: { onBackToToday?: () => void } = {}) {
  const { sessionConfig } = useUserStore();
  const { statusFilters, searchQuery, toggleStatusFilter, clearStatusFilters, setSearchQuery } = useProjectStore();
  const [showCreate, setShowCreate] = useState(false);

  // Needs both slices: the Parking Lot itself (its own section below) and
  // the regular projects (grid + status filter counts) — see
  // useRegularProjects() in hooks/useProjects.ts.
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: regularProjectsData } = useRegularProjects();

  const effectiveRole = useEffectiveRole();

  const effectiveRoleRef = useRef(effectiveRole);
  effectiveRoleRef.current = effectiveRole;

  // Mount/unmount tracking
  useEffect(() => {
    const start = Date.now();
    track('dashboard.pm.opened', 'page_view', { role: effectiveRoleRef.current });
    return () => {
      track('dashboard.pm.time_spent', 'page_view', {
        role: effectiveRoleRef.current,
        durationMs: Date.now() - start,
      });
    };
  }, []);

  // Status filter change tracking
  const prevStatusFiltersRef = useRef(statusFilters);
  useEffect(() => {
    if (prevStatusFiltersRef.current !== statusFilters) {
      track('dashboard.pm.filter_changed', 'action', {
        role: effectiveRoleRef.current,
        filters: statusFilters.join(','),
      });
      prevStatusFiltersRef.current = statusFilters;
    }
  }, [statusFilters]);

  const parkingLotProjects = (projects ?? []).filter((p) => p.isParkingLot === true);
  const regularProjects = regularProjectsData ?? [];

  const filteredProjects = regularProjects.filter((p) => {
    // Status filter — OR logic across selected statuses
    if (statusFilters.length > 0) {
      const matchesStatus = statusFilters.some((f) => STATUS_GROUPS[f].includes(p.status));
      if (!matchesStatus) return false;
    }
    // Search — matches project name or owner name
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchesName = p.name.toLowerCase().includes(q);
      const matchesOwner = p.ownerName?.toLowerCase().includes(q) ?? false;
      if (!matchesName && !matchesOwner) return false;
    }
    return true;
  });

  // Counts based on full unfiltered list (chips show totals, not filtered totals)
  const filterOptions: FilterOption[] = FILTER_OPTIONS.map((opt) => ({
    ...opt,
    count: regularProjects.filter((p) =>
      STATUS_GROUPS[opt.value as StatusFilterKey].includes(p.status)
    ).length,
  }));

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <DashboardHeader title="Portfolio Health" roleLabel="Product Management" onBackToToday={onBackToToday} />

      {/* Filters + Search */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        {/* Search input — left, grows to fill space */}
        <div className="relative flex-1 min-w-[240px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by project or owner…"
            className="w-full pl-8 pr-8 py-1.5 rounded-full text-meta bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Status filter chips — right aligned */}
        <FilterCard
          options={filterOptions}
          selected={statusFilters}
          onToggle={(v) => toggleStatusFilter(v as StatusFilterKey)}
          onClearAll={clearStatusFilters}
        />
      </div>

      {/* Project grid */}
      {projectsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-6">
          {[...Array(6)].map((_, i) => (
            <ProjectSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-6">
          {filteredProjects.map((project: Project) => (
            <RiskCard key={project.projectId} project={project} />
          ))}

          {/* Add project card */}
          {(sessionConfig?.entityRole === 'admin' ||
            sessionConfig?.role === 'admin' ||
            sessionConfig?.functionalRoles?.includes('product_manager') ||
            sessionConfig?.role === 'product_manager' ||
            sessionConfig?.functionalRoles?.includes('executive') ||
            sessionConfig?.role === 'executive') && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowCreate(true)}
              className="bg-white dark:bg-bg-surface border-2 border-dashed border-gray-200 dark:border-white/10 hover:border-primary/40 dark:hover:border-primary/30 hover:bg-gray-50 dark:hover:bg-white/[0.02] rounded-xl p-5 transition-all flex flex-col items-center justify-center gap-2 min-h-[160px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <div className="h-10 w-10 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                <Plus className="h-5 w-5" />
              </div>
              <span className="text-meta font-medium">Add Project</span>
            </motion.button>
          )}

          {filteredProjects.length === 0 && !projectsLoading && (
            <div className="col-span-full text-center py-16 text-gray-500">
              <p className="text-body font-medium">No projects match this filter.</p>
            </div>
          )}
        </div>
      )}

      {/* Parking Lot section */}
      {parkingLotProjects.length > 0 && (
        <div className="mt-10">
          <div className="mb-4">
            <h2 className="text-title text-gray-900 dark:text-white">
              Parking Lot
            </h2>
            <p className="text-meta text-gray-500 dark:text-gray-500 mt-0.5">
              Unplanned items not yet assigned to a project
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-6">
            {parkingLotProjects.map((project: Project) => (
              <RiskCard key={project.projectId} project={project} />
            ))}
          </div>
        </div>
      )}

      {showCreate && (
        <AddProjectModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}
